<template>
  <component :is="tag">
    <!-- content -->
    <div 
      class="fts-popper" 
      :class="{'append-to-body': appendToBody}" 
      ref="content">
      <transition :name="transition" :enter-active-class="enterActiveClass" :leave-active-class="leaveActiveClass" @after-leave="doDestroy">
        <div 
          class="popper-content fts-scrollbar" 
          :class="contentClass"   
          @click="clickHandler"
          v-if="showPopper && !disabled">
          <slot :select="select">{{content}}</slot>
        </div>
      </transition>
    </div>
    <!-- reference -->
    <slot name="reference"></slot>
  </component>
</template>

<script>
import {createPopper} from '@popperjs/core';
import commons from '../commons/commons';

function on(element, event, handler) {
  if (element && event && handler) {
    document.addEventListener ? element.addEventListener(event, handler, false) : element.attachEvent('on' + event, handler);
  }
}
function off(element, event, handler) {
  if (element && event) {
    document.removeEventListener ? element.removeEventListener(event, handler, false) : element.detachEvent('on' + event, handler)
  }
}

export default {
  props: { 
    tag: {
      type: String,
      default: 'div'
    },
    appendToBody: Boolean,
    trigger: {
      type: String,
      default: 'clickToToggle',
      validator: value => [
        'clickToOpen',
        'click', /* same as clickToToggle, provided for backwards compatibility. */
        'clickToToggle',
        'hover',
        'focus'
      ].indexOf(value) > -1
    },
    content: String,  
    /* placement */
    placement: String,
    /* strategy */
    strategyFixed: Boolean,
    /* prevent overflow */
    preventOverflowAltAxis: Boolean,
    preventOverflowTetherOff: Boolean,
    preventOverflowBoundary: String,
    /* flip */
    flip: {
      type: Boolean,
      default: true,
    },
    /* offset */
    offset: {
      type: Array,
      default: () => [0, 0]
    },
    /* mouse */
    delayOnMouseOver: {
      type: Number,
      default: 10,
    },
    delayOnMouseOut: {
      type: Number,
      default: 10,
    },
    /* disabled */
    disabled: Boolean,
    /* transition */
    transition: {
      type: String,
      default: ''
    },
    enterActiveClass: String,
    leaveActiveClass: String,
    /* style / class */
    contentClass: String,
    zIndex: Number,
    /* event */
    stopPropagation: Boolean,
    preventDefault: Boolean,
    /* escape handler use */
    stopPropagationSiblingClass: Boolean,
    stopPropagationClass: [String, Number],
    /* before */
    beforeOpen: Function,
  },
  data: () => ({
    showPopper: false,
    triggerElm: null,
    popperInstance: null,
    popperOptions: {
      placement: 'bottom',
      computeStyle: {
        gpuAcceleration: false
      },
      modifiers: []
    }
  }),
  methods: {
    async doShow() {
      try {
        this.beforeOpen && await this.beforeOpen();
        this.showPopper = true;
      } catch(err) {
        console.warn('Popper', err);
      }
    },
    async doToggle() {
      if (this.showPopper) {
        this.showPopper = false;
      } else {
        try {
          this.beforeOpen && await this.beforeOpen();
          this.showPopper = true;
        } catch(err) {
          console.warn('Popper', err);
        }   
      }
    },
    onMouseOver() {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        this.showPopper = true;
      }, this.delayOnMouseOver);
    },
    onMouseOut() {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        this.doClose();
      }, this.delayOnMouseOut);
    },
    doClose() {
      this.showPopper = false;
    },
    doDestroy() {
      /* remove child */
      if (this.popperContent) {
        if (this.appendedToBody) {
          this.appendedToBody = false;
          try {
            document.body.removeChild(this.popperContent);
          } catch(err) {}
        } else {
          try {
            this.$el.removeChild(this.popperContent);
          } catch(err) {}
        }
      }
      /* destory popper instance */
      if (this.popperInstance) {
        this.popperInstance.destroy();
        this.popperInstance = null;
        off(document, 'mousedown', this.escapeHandler);
      }
    },
    select(val) {
      this.doClose();
      this.$emit('select', val);
    },
    /* handler */
    escapeHandler(e) {
      if (!this.$el || !this.triggerElm ||
        this.elementContains(this.$el, e.target) ||
        this.elementContains(this.triggerElm, e.target) ||
        !this.popperContent || this.elementContains(this.popperContent, e.target) || 
        (this.stopPropagationSiblingClass && commons.elementStopPropagation({ className: 'popper-content' })) ||
        (this.stopPropagationClass && commons.elementStopPropagation({ className: this.stopPropagationClass }))
      ) return;

      this.doClose();
    },
    clickHandler(event) {
      if (this.stopPropagation)
        event.stopPropagation()
      
      if (this.preventDefault) 
        event.preventDefault()
    },
    elementContains(elm, otherElm) {
      if (typeof elm.contains === 'function') {
        return elm.contains(otherElm);
      }
      return false;
    },
    /* popper */
    createPopper() {
      this.popperContent = this.$refs.content
      /* trigger */
      switch (this.trigger) {
        case 'clickToOpen':
          on(document, 'mousedown', this.escapeHandler);
          break;
        case 'click':
        case 'clickToToggle':
          on(document, 'mousedown', this.escapeHandler);
          break;
        case 'hover':
          on(this.popperContent, 'mouseover', this.onMouseOver);
          on(this.popperContent, 'mouseout', this.onMouseOut);
          break;
        case 'focus':
          on(this.popperContent, 'focus', this.onMouseOver);
          on(this.popperContent, 'blur', this.onMouseOut);
          break;
      }
      /* append child */
      if (this.appendToBody) {
        this.appendedToBody = true;
        document.body.appendChild(this.popperContent);
      } else {
        this.$el.appendChild(this.popperContent);
      }
      /* placement */
      this.popperOptions = Object.assign(this.popperOptions, {
        placement: this.placement,
      });
      /* type Strategy = 'fixed' | 'absolute' */
      /* default: absolute */
      if (this.strategyFixed) 
        this.popperOptions.strategy = 'fixed';
      /* modifiers */
      this.popperOptions.modifiers = [
        {
          name: 'preventOverflow',
          options: {
            altAxis: this.preventOverflowAltAxis, /* default: false */
            tether: !this.preventOverflowTetherOff, /* default: true */
          },
        },
        {
          name: 'flip',
          enabled: this.flip
        },
        {
          name: 'offset',
          options: {
            offset: this.offset,
          }
        }
      ];
      /* type Boundary = 'clippingParents' | HTMLElement | Array<HTMLElement>; */
      /* default: clippingParents */
      if (this.preventOverflowBoundary && this.preventOverflowBoundary !== 'clippingParents') {
        try {
          const customBoundary = document.querySelector(`#${this.preventOverflowBoundary}`);
          let preventOverflow = this.popperOptions.modifiers.find(x => x.name === 'preventOverflow');
          preventOverflow.options.boundary = customBoundary;
        } catch(err) {
          console.warn(`[Warn] ${err}`);
        }
      }
      /* set style */
      if (this.appendToBody || this.strategyFixed) 
        Object.assign(this.popperContent.style, {
          "max-height": "100%",
          "max-width": "100%",
          "display": "flex",
          "flex-direction": "column",
        });
      /* z-index */
      if (this.zIndex)
        this.popperContent.style.zIndex = this.zIndex;
      /* create popper */
      this.popperInstance = createPopper(this.triggerElm, this.popperContent, this.popperOptions);
      this.$emit('created', this);
    },
  },
  watch: {
    showPopper(value) {
      if (value) {      
        this.createPopper();
        this.$emit('show', this);
      } else {
        this.$emit('hide', this);
      }
    },
  },
  mounted() {
    this.triggerElm = this.$slots.reference[0].elm;
    /* add trigger event listener */
    switch (this.trigger) {
      case 'clickToOpen':
        on(this.triggerElm, 'click', this.doShow);
        break;
      case 'click': /* same as clickToToggle, provided for backwards compatibility. */
      case 'clickToToggle':
        on(this.triggerElm, 'click', this.doToggle);
        break;
      case 'hover':
        on(this.triggerElm, 'mouseover', this.onMouseOver);
        on(this.triggerElm, 'mouseout', this.onMouseOut);
        break;
      case 'focus':
        on(this.triggerElm, 'focus', this.onMouseOver);
        on(this.triggerElm, 'blur', this.onMouseOut);
        break;
    }
  },
  beforeDestroy() {
    this.doDestroy();
    /* remove trigger event listener */
    off(this.triggerElm, 'click', this.doToggle);
    off(this.triggerElm, 'mouseup', this.doClose);
    off(this.triggerElm, 'mousedown', this.doShow);
    off(this.triggerElm, 'focus', this.doShow);
    off(this.triggerElm, 'blur', this.doClose);
    off(this.triggerElm, 'mouseout', this.onMouseOut);
    off(this.triggerElm, 'mouseover', this.onMouseOver);
  },
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-popper {
  &.append-to-body {
    z-index: $z-index-popover;
  }
  &:not(.append-to-body) {
    z-index: $z-index-dropdown;
  }
}

.popper-content {
  max-height: 100%;
  max-width: 100%;
  padding: .375rem 0;
  box-shadow: var(--fts-elevation-3);
  border-radius: .25rem;
  background: var(--fts-popover-background);
  color: var(--fts-text);
  font-size: .875rem;
}
</style>